import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Users, ArrowLeft, Building2, ExternalLink, Loader2 } from 'lucide-react';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import { API } from '@/App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnimatedCard = ({ member, delay, size = 'md' }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const avatarSizes = { lg: 'w-28 h-28 sm:w-32 sm:h-32', md: 'w-20 h-20 sm:w-24 sm:h-24' };
  const initials = member.name?.split(' ').filter(n => n.length > 1).map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  const photoSrc = member.photo_url
    ? (member.photo_url.startsWith('http') ? member.photo_url : `${BACKEND_URL}${member.photo_url}`)
    : null;

  return (
    <div ref={ref} className="flex flex-col items-center transition-all duration-700 ease-out group"
      style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: `${delay}ms` }}
      data-testid={`member-card-${member.name?.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className={`${avatarSizes[size]} rounded-full overflow-hidden shadow-lg mb-3 ring-4 ring-white`}>
        {photoSrc ? (
          <img src={photoSrc} alt={member.name} className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
        ) : null}
        <div className={`w-full h-full bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center text-white font-bold ${size === 'lg' ? 'text-2xl' : 'text-xl'}`}
          style={{ display: photoSrc ? 'none' : 'flex' }}>
          {initials}
        </div>
      </div>
      <h3 className={`font-bold text-gray-900 text-center ${size === 'lg' ? 'text-sm sm:text-base' : 'text-xs sm:text-sm'}`} style={{ fontFamily: "'Barlow', sans-serif" }}>
        {member.name}
      </h3>
      {member.role && <p className="text-[#35AFE4] text-xs font-semibold text-center mt-0.5">{member.role}</p>}
      {(member.practitioner_slug || member.user_id || member.fiche_id) && (
        <Link
          to={`/annuaire/${member.practitioner_slug || member.user_id || member.fiche_id}`}
          className="inline-flex items-center gap-1 mt-1.5 px-3 py-1 text-[11px] font-semibold text-[#35AFE4] hover:text-white bg-[#eaf6fc] hover:bg-[#35AFE4] rounded-full transition-all duration-200"
          data-testid={`member-profile-link`}
        >
          <ExternalLink size={10} />
          Voir la fiche
        </Link>
      )}
    </div>
  );
};

const RegionPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [region, setRegion] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/regions/${slug}`)
      .then(r => setRegion(r.data))
      .catch(() => navigate('/'))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-[#35AFE4]" />
      </div>
    );
  }

  if (!region) return null;

  const bureauData = region.bureau && region.bureau.length > 0 ? region.bureau : region.members || [];
  const hasMembers = bureauData.length > 0;
  const hasDepartments = region.departments && region.departments.length > 0;

  // Sort: président first, then VP, secrétaire, trésorier, then others
  const roleOrder = ['president', 'presidente', 'vice-president', 'vice-presidente', 'secretaire', 'tresorier', 'tresoriere', 'secretaire adjoint', 'tresorier adjoint', 'tresoriere adjointe', 'membre du ca', 'suppleant', 'suppleante', 'membre'];
  const sortedMembers = [...bureauData].sort((a, b) => {
    const ra = (a.role || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const rb = (b.role || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const ia = roleOrder.findIndex(r => ra.includes(r));
    const ib = roleOrder.findIndex(r => rb.includes(r));
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

  // Split: bureau principal (président, VP, secrétaire, trésorier) + conseil
  const mainRoles = ['president', 'presidente', 'vice-president', 'vice-presidente', 'secretaire', 'tresorier', 'tresoriere'];
  const mainMembers = sortedMembers.filter(m => {
    const r = (m.role || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return mainRoles.some(mr => r.includes(mr)) && !r.includes('adjoint');
  });
  const otherMembers = sortedMembers.filter(m => !mainMembers.includes(m));

  const logoSrc = region.logo_url
    ? (region.logo_url.startsWith('http') ? region.logo_url : `${BACKEND_URL}${region.logo_url}`)
    : null;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Roboto', sans-serif" }} data-testid="region-page">
      <SEO title={`${region.name} - SFOALC`} description={`Antenne régionale SFOALC ${region.name}`} />
      <SiteHeader />

      {/* Hero — même style que PublicBureauPage */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2a6b8f 50%, #35AFE4 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-40 w-72 h-72 rounded-full bg-white/20" />
          <div className="absolute -bottom-20 -left-20 w-60 h-60 rounded-full bg-white/10" />
        </div>
        <div className="max-w-5xl mx-auto px-4 pt-28 pb-16 text-center relative z-10">
          {/* Bouton retour discret */}
          <button onClick={() => navigate(-1)} className="absolute left-4 top-28 inline-flex items-center gap-2 text-white/50 hover:text-white text-sm transition-all" data-testid="back-btn">
            <ArrowLeft size={16} /> Retour
          </button>

          {/* Logo de la région */}
          {logoSrc && (
            <div className="flex justify-center mb-5">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/10 backdrop-blur-sm border-4 border-white/20 overflow-hidden flex items-center justify-center">
                <img src={logoSrc} alt={region.name} className="w-full h-full object-cover"
                  onError={e => { e.target.parentElement.style.display = 'none'; }}
                  data-testid="region-logo" />
              </div>
            </div>
          )}

          <p className="text-[#8ec8e0] text-xs font-bold uppercase tracking-[0.25em] mb-4">
            <MapPin size={12} className="inline mr-1" />
            Antenne Régionale
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Barlow', sans-serif" }} data-testid="region-title">
            {region.name}
          </h1>
          {region.year_created && (
            <p className="text-white/50 text-sm">Créée en {region.year_created}</p>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1000 60" preserveAspectRatio="none" style={{ display: 'block', width: '100%', height: '40px' }}>
            <path fill="#ffffff" d="M0,60V50C0.9,33,126.7,0,500,0S999.1,33,1000,50V60H0Z" />
          </svg>
        </div>
      </section>

      {/* Départements — style cartes comme le bureau national */}
      {hasDepartments && (
        <section className="py-12 px-4 -mt-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-[#f4fafe] rounded-xl p-6 border border-[#d0e8f0]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#35AFE4] flex items-center justify-center">
                  <Building2 size={20} className="text-white" />
                </div>
                <h3 className="font-bold text-[#1e3a5f] text-sm" style={{ fontFamily: "'Barlow', sans-serif" }}>Départements couverts</h3>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="departments">
                {region.departments.map((d, i) => (
                  <span key={i} className="px-4 py-2 bg-white text-[#1e3a5f] rounded-full text-sm font-medium border border-[#d0e8f0]">
                    {d}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Description / Présentation */}
      {region.description && (
        <section className="py-8 px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-gray-600 leading-relaxed text-sm sm:text-base prose prose-sm max-w-none" 
              dangerouslySetInnerHTML={{ __html: region.description }} 
              data-testid="region-description" />
            {region.address && (
              <div className="mt-6 flex items-start gap-2 text-sm text-gray-500">
                <MapPin size={16} className="flex-shrink-0 mt-0.5 text-[#35AFE4]" />
                <span>{region.address}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Bureau principal — même style que PublicBureauPage */}
      {mainMembers.length > 0 && (
        <section className="py-14 px-4 bg-[#f8fbfd]" data-testid="bureau-members-section">
          <div className="max-w-4xl mx-auto">
            <p className="text-[#35AFE4] text-xs font-bold uppercase tracking-[0.2em] text-center mb-2">Direction</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-12" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Le Bureau
            </h2>
            <div className={`grid gap-8 sm:gap-10 ${mainMembers.length <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-5'} justify-items-center`}>
              {mainMembers.map((m, i) => (
                <AnimatedCard key={m.name + i} member={m} delay={i * 150} size="lg" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Autres membres — même style que la section conseil */}
      {otherMembers.length > 0 && (
        <section className="py-14 px-4" data-testid="council-members-section">
          <div className="max-w-5xl mx-auto">
            <p className="text-[#35AFE4] text-xs font-bold uppercase tracking-[0.2em] text-center mb-2">Membres</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-center text-gray-900 mb-12" style={{ fontFamily: "'Barlow', sans-serif" }}>
              {mainMembers.length > 0 ? "Les autres membres du bureau" : "Le Bureau"}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-8 sm:gap-10 justify-items-center">
              {otherMembers.map((m, i) => (
                <AnimatedCard key={m.name + i} member={m} delay={i * 80} size="md" />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Si aucun membre */}
      {!hasMembers && (
        <section className="py-14 px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-[#eaf6fc] flex items-center justify-center mx-auto mb-4">
              <Users size={28} className="text-[#35AFE4]" />
            </div>
            <h2 className="text-lg font-bold text-[#1e3a5f] mb-2" style={{ fontFamily: "'Barlow', sans-serif" }}>
              Bureau Régional
            </h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Les informations de l'organigramme du bureau de cette antenne seront prochainement disponibles.
            </p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default RegionPage;
